import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import Card from '../components/Card';
import { apiClient } from '../services/api';
import { Job } from '../types';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchJobs = async () => {
    try {
      const fetchedJobs = await apiClient.getJobs();
      setJobs(fetchedJobs);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch jobs:', err);
      setError('Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();

    // Poll for updates every 30 seconds
    const interval = setInterval(() => {
      fetchJobs();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleDelete = async (jobId: string) => {
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }

    try {
      await apiClient.deleteJob(jobId);
      setJobs(jobs.filter(job => job.jobId !== jobId));
    } catch (err: any) {
      console.error('Failed to delete job:', err);
      alert('Failed to delete job. Please try again.');
    }
  };

  const handleDownload = async (jobId: string, fileType: string) => {
    try {
      const response = await apiClient.getDownloadUrl(jobId, fileType);
      // Use location.href for direct download instead of opening in new tab
      window.location.href = response.downloadUrl;
    } catch (err: any) {
      console.error('Failed to get download URL:', err);
      alert('Failed to download file. Please try again.');
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatFileSize = (bytes: number) => {
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = (status: string) => {
    const statusClasses: { [key: string]: string } = {
      uploaded: 'status-pending',
      audio_extracted: 'status-processing',
      transcribing: 'status-processing',
      translating: 'status-processing',
      generating_output: 'status-processing',
      completed: 'status-completed',
      failed: 'status-failed',
      queued: 'status-queued',
    };

    return (
      <span className={`status-badge ${statusClasses[status] || 'status-pending'}`}>
        {status.replace(/_/g, ' ')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="container">
          <h1>Dashboard</h1>
          <Card>
            <p>Loading jobs...</p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="container">
        <div className="dashboard-header">
          <h1>Dashboard</h1>
          <Link to="/upload">
            <Button variant="primary">Upload New File</Button>
          </Link>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <Card>
          <h2>Your Transcription Jobs</h2>
          {jobs.length === 0 ? (
            <p className="text-secondary">
              Your transcription jobs will appear here. Upload your first file to get started!
            </p>
          ) : (
            <div className="jobs-list">
              {jobs.map(job => (
                <div key={job.jobId} className="job-item">
                  <div className="job-header">
                    <div className="job-info">
                      <h3>{job.fileName}</h3>
                      <div className="job-meta">
                        <span className="job-meta-item">
                          <strong>Type:</strong> {job.mediaType}
                        </span>
                        <span className="job-meta-item">
                          <strong>Size:</strong> {formatFileSize(job.fileSize)}
                        </span>
                        <span className="job-meta-item">
                          <strong>Duration:</strong> {formatDuration(job.duration)}
                        </span>
                        <span className="job-meta-item">
                          <strong>Uploaded:</strong> {formatDate(job.uploadTimestamp)}
                        </span>
                      </div>
                    </div>
                    <div className="job-status">
                      {getStatusBadge(job.status)}
                      {job.queuePosition && (
                        <span className="queue-position">
                          Queue position: {job.queuePosition}
                        </span>
                      )}
                    </div>
                  </div>

                  {job.errorMessage && (
                    <div className="job-error">
                      <strong>Error:</strong> {job.errorMessage}
                    </div>
                  )}

                  <div className="job-actions">
                    {job.status === 'completed' && (
                      <>
                        <Button
                          variant="primary"
                          size="small"
                          onClick={() => handleDownload(job.jobId, job.mediaType === 'audio' ? 'swahili' : 'swahili-srt')}
                        >
                          Download Swahili
                        </Button>
                        <Button
                          variant="primary"
                          size="small"
                          onClick={() => handleDownload(job.jobId, job.mediaType === 'audio' ? 'english' : 'english-srt')}
                        >
                          Download English
                        </Button>
                      </>
                    )}
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => handleDelete(job.jobId)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
